import { Dropdown } from '@toggle/design-system';
import { keyboard, KeyboardKeys } from '@toggle/helpers';
import { EntitySearchType } from '@toggle/toggle';
import React, {
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import {
  ChatDropdown,
  ChatDropdownItem,
} from '~/components/chat-dropdown-item/ChatDropdownItem';
import { ChatInput } from '~/components/chat-input/ChatInput';
import { useRows } from '~/components/chat-input/useRows';
import { useTagsSearch } from '~/hooks/use-tags-search/useTagsSearch';
import { useApp } from '~/stores/use-app/useApp';

import { TagSubmitButton } from './submit-button/TagSubmitButton';
import * as S from './TagSubmit.styles';
import * as ChatShared from './TagSubmitShared.styles';
import { mapEntityItems } from './utils/searchUtils';

export interface TagSubmitProps {
  className?: string;
  placeholder?: string;
  isSimpleVersion?: boolean;
  showClear?: boolean;
  headerText: string;
  onSubmit: (
    e: React.FormEvent<HTMLFormElement>,
    inputValue: string,
    clear: () => void
  ) => void;
  isLoading: boolean;
  onKeyDown?: (
    e: React.KeyboardEvent<HTMLElement>,
    inputValue: string,
    clear: () => void
  ) => void;
  onAbort?: () => void;
  onReset?: () => void;
  onSelect: (item: ChatDropdown, clear: () => void) => void;
}

const isAssetNameMessage = (value: string) =>
  value.length < 16 && value.split(/\s+/).length < 3;

export const TagSubmit = forwardRef<HTMLTextAreaElement, TagSubmitProps>(
  (
    {
      className,
      placeholder,
      isSimpleVersion,
      headerText,
      onSubmit,
      isLoading,
      onKeyDown,
      onAbort,
      onReset,
      onSelect,
      showClear = true,
    }: TagSubmitProps,
    ref
  ) => {
    const [inputValue, setInputValue] = useState('');
    const [showItems, setShowItems] = useState(true);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const { rows, updateRows } = useRows({ value: inputValue });

    const { isSidebarExpanded } = useApp(state => ({
      isSidebarExpanded: state.isSidebarExpanded,
    }));
    const isAssetMessage = useRef(false);
    const {
      tags: tagList,
      searchTags,
      clearTags,
    } = useTagsSearch({
      searchType: EntitySearchType.Entity,
      additionalConditionChecker: isAssetNameMessage,
    });

    useImperativeHandle(ref, () => inputRef.current as HTMLTextAreaElement, [
      inputRef,
    ]);

    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      } else {
        clearInput();
      }
    }, [inputRef]);

    const clearInput = () => {
      setInputValue('');
    };

    const clearDropdown = () => {
      clearInput();
      clearTags();
      setShowItems(false);
    };

    const onTagSubmit = (event: React.FormEvent<HTMLFormElement>) => {
      onSubmit?.(event, inputValue, clearDropdown);
    };

    const onInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = e.target;
      isAssetMessage.current = isAssetNameMessage(value);
      setInputValue(value);
      setShowItems(true);
      !isSimpleVersion && searchTags(value);
    };

    const renderDropdownItem = (
      item: ChatDropdown,
      isActive: boolean,
      isFocused: boolean
    ) => (
      <ChatDropdownItem
        key={item.id}
        item={item}
        isActive={isActive}
        isFocused={isFocused}
        highlighted={inputValue}
      />
    );

    const handleDropdownKeyDown = (
      key: KeyboardKeys,
      item: ChatDropdown | null,
      _: number,
      e: React.KeyboardEvent<HTMLElement>
    ) => {
      if (isAssetMessage.current && tagList.length > 0) {
        return;
      }
      if (item && (key === 'ArrowDown' || key === 'ArrowUp')) {
        setInputValue(item.leftText);
      }

      if (
        e.key === keyboard.enter.key &&
        !e.shiftKey &&
        !e.nativeEvent.isComposing // fix: [861n3tz5g] Japanese IME: not finished question is sent via 'enter'
      ) {
        // Let user skip line when message is pending
        if (!isLoading) {
          e.preventDefault();
        }
        onKeyDown?.(e, inputValue, clearDropdown);
      }
    };

    const onSelectItem = (item: ChatDropdown) => {
      onSelect(item, clearDropdown);
    };

    const resetChat = () => {
      onReset?.();
      clearInput();
      setShowItems(false);
    };

    const items: ChatDropdown[] = mapEntityItems(tagList.slice(0, 5));

    const displayResetIcon = showClear && !isSimpleVersion;
    const x = showClear ? -70 : -10;

    return (
      <S.Form
        onSubmit={onTagSubmit}
        className={className}
        $isSidebarExpanded={isSidebarExpanded}
      >
        <ChatShared.LoaderWrapper $isMessagePending={isLoading}>
          <Dropdown
            dropdownItems={items}
            shouldCloseOnOutsideClick
            renderDropdownItem={renderDropdownItem}
            selectItem={onSelectItem}
            shouldShowItems={showItems}
            placement="top-start"
            offsetValue={{ x, y: 32 }}
            beforeInListNode={
              !!items.length && (
                <S.DropdownHeader>{headerText}</S.DropdownHeader>
              )
            }
            hasV2Styles={false}
            shouldSetActiveOnSelect={false}
            onChildKeyDown={handleDropdownKeyDown}
          >
            <ChatInput
              ref={inputRef}
              data-testid="chat-input"
              autoFocus
              placeholder={placeholder}
              value={inputValue}
              onChange={onInputChange}
              displayReset={displayResetIcon}
              resetChat={resetChat}
              rows={rows}
              updateRows={updateRows}
              isMessagePending={isLoading}
            >
              <TagSubmitButton
                inputValue={inputValue}
                rows={rows}
                isMessagePending={isLoading}
                abortRequest={onAbort}
              />
            </ChatInput>
          </Dropdown>
        </ChatShared.LoaderWrapper>
      </S.Form>
    );
  }
) as ForwardRefExoticComponent<
  TagSubmitProps & RefAttributes<HTMLTextAreaElement>
> & {
  Styled: typeof S;
};

TagSubmit.Styled = S;
